import { calculateVatBreakdown, TaxTreatment } from './vatBreakdown'

export type PurchaseTaxBreakdown =
    | { kind: 'vat'; rate: number; net: number; vat: number; gross: number }
    | { kind: 'exempt'; exemptType: 'objective' | 'subjective'; gross: number }
    | { kind: 'margin'; gross: number }

// Pure: the tax rows an order summary shows under the item list. Shared so the app's purchase
// screen and the web checkout break the same total down the same way.
//
// Null means "render nothing", which is what every purchase that is not an event gets — the
// no-regression guarantee for subscriptions, chats, packages and products is structural rather
// than a promise. A free or not-yet-priced purchase has nothing to break down either.
export const buildPurchaseTaxBreakdown = (
    price: number | undefined,
    tax: TaxTreatment | null | undefined,
): PurchaseTaxBreakdown | null => {
    if (!tax || price === undefined || price <= 0) {
        return null
    }

    // A mixed basket has no single rate to name, and this screen only ever renders one item's
    // treatment, so there is nothing here that could produce one. Render nothing rather than
    // a percentage that is not the whole story.
    if (tax.kind === 'mixed') {
        return null
    }

    if (tax.kind === 'exempt') {
        return { kind: 'exempt', exemptType: tax.exemptType, gross: price }
    }

    if (tax.kind === 'margin') {
        return { kind: 'margin', gross: price }
    }

    const { net, vatAmount, gross } = calculateVatBreakdown(price, tax)

    return { kind: 'vat', rate: tax.rate, net, vat: vatAmount, gross }
}
